import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import SectionHeader from "../../../../util/component/SectionHeader";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { DialogInterface, SelectElementModelBE, SelectElementModelFE } from "../../api/types/generic";
import dayjs from "dayjs";
import {
  AncillaryExceptionEntityResponse,
  FormSearchAncillaryExceptionModel
} from "../../api/types/ancillaryException";
import { AgGridReact } from "ag-grid-react";
import { CommercialCategoryEntityResponse, CommercialCategorySelectFE } from "../../api/types/commercialCategory";
import { commercialCategoryListAtom } from "../../state/commercialCategoriesRecoil";
import { commercialSegmentListAtom } from "../../state/commercialSegmentsAtom";
import {
  dateFormatter,
  fromAncillaryBeToFe,
  fromGenericBeToGenericFe,
  i18nConvertNumberWithDecimals,
  useActionColDef
} from "../../utils";
import { DEFAULT_COL_DEF, SET_SNACKBAR } from "../../constants";
import { useCommercialCategoryRepository } from "../../api/repositories/CommercialCategoryRepository";
import { useCommercialSegmentRepository } from "../../api/repositories/CommercialSegmentRepository";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { useAgGridLocale } from "../../../../config/globalConfig";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { useAncillaryExceptionRepository } from "../../api/repositories/AncillaryExceptionRepository";
import BasicToolbar from "../../common/components/BasicToolbar";
import { ancillaryListAtom } from "../../state/ancillariesRecoil";
import ActionsPopover from "../../common/components/ActionsPopover";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import DialogComponent from "../../common/components/DialogComponent";
import { AncillaryEntityResponse, AncillarySelectFE } from "../../api/types/ancillary";
import Loading from "../../common/components/Loading";
import AncillaryExceptionContainerView from "../AncillaryException/AncillaryExceptionContainerView";
import { useAncillaryRepository } from "../../api/repositories/AncillaryRepository";
import AncillaryExceptionFilters from "./Filters/AncillaryExceptionFilters";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import Chip from "@mui/material/Chip";

const AncillaryExceptions = () => {
  // Use Translation
  const { t, i18n } = useTranslation(["translation", "pricing"]);

  // Use Context
  const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);

  // Use AgGrid Locale
  const agGridLocale = useAgGridLocale();

  // Use Ref
  const gridRef = useRef<AgGridReact>(null);

  const [, setCommercialCategoryList] = useRecoilState(
    commercialCategoryListAtom
  );
  const [, setCommercialSegmentList] = useRecoilState(
    commercialSegmentListAtom
  );
  const [, setAncillaryList] = useRecoilState(ancillaryListAtom);
  // ITDRIV-20190 / LPP-23039: lista completa accessori filtrata per perimetro utente (backend)
  const [ancillaryListFull, setAncillaryListFull] = useState<AncillarySelectFE[]>([]);

  // Use Repository Hooks
  const {
    getAncillaryExceptionsPaged,
    deleteAncillaryException,
    terminateAncillaryException
  } = useAncillaryExceptionRepository();
  const { getAncillariesBySegmentId } = useAncillaryRepository();
  const { getCommercialCategories } = useCommercialCategoryRepository();
  const { getCommercialSegments } = useCommercialSegmentRepository();

  // Use State
  const [viewAncillaryException, setViewAncillaryException] =
    useState<boolean>(false);
  const [rowIndex, setRowIndex] = useState<number | null>(null);
  const [dialog, setDialog] = useState<DialogInterface | null>(null);
  const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = 
    useState<CommercialCategorySelectFE[]>([]);

  const handleGetStaticContent = async () => {
    const commercialCategoriesData: CommercialCategoryEntityResponse[] =
      await getCommercialCategories();

    const commercialCategoryList: CommercialCategorySelectFE[] =
      fromGenericBeToGenericFe(commercialCategoriesData);
    // Non settiamo il Recoil atom con name generico - i filtri lo gestiranno con labels
    setCommercialCategoryListOriginal(commercialCategoryList);

    const commercialSegmentsData = await getCommercialSegments();

    const commercialSegmentList: SelectElementModelFE[] =
      fromGenericBeToGenericFe(commercialSegmentsData);
    setCommercialSegmentList(commercialSegmentList);

    // ITDRIV-20190 / LPP-23039: carica solo accessori del perimetro utente (LE + Dealer)
    const ancillariesData: AncillaryEntityResponse[] = await getAncillariesBySegmentId({
      dealers: user.dealers.map((d: SelectElementModelBE) => d.name).filter((n: string) => n !== "all"),
      legalEntities: user.legalEntities.map((le: SelectElementModelBE) => le.name).filter((n: string) => n !== "all")
    });
    const ancillaryList: AncillarySelectFE[] =
      fromAncillaryBeToFe(ancillariesData);

    // ITDRIV-20190 / LPP-23039: disambigua le voci con nameAncillary uguale (ignorecase)
    // aggiungendo il suffisso del mercato (es. "159 - Second Driver FR (fr)").
    // Solo se l'utente ha piu' di 1 LE e le voci duplicate appartengono a LE diverse.
    const userHasMultipleLE = user.legalEntities.filter((le: SelectElementModelBE) => le.name !== "all").length > 1;
    let ancillaryListDisambiguated = ancillaryList;

    if (userHasMultipleLE) {
      // Raggruppa per nameAncillary (ignorecase) → set di LE per ogni nome
      const nameToLEs = new Map<string, Set<string>>();
      ancillaryList.forEach((anc) => {
        const key = ((anc as any).nameAncillary || "").toLowerCase();
        if (!nameToLEs.has(key)) nameToLEs.set(key, new Set());
        const le = typeof anc.legalEntity === "string" ? anc.legalEntity : "";
        if (le) nameToLEs.get(key)!.add(le);
      });

      ancillaryListDisambiguated = ancillaryList.map((anc) => {
        const key = ((anc as any).nameAncillary || "").toLowerCase();
        const leSet = nameToLEs.get(key);
        // Aggiunge suffisso solo se ci sono 2+ LE diverse per lo stesso nome
        if (leSet && leSet.size > 1 && anc.legalEntity) {
          const le = typeof anc.legalEntity === "string" ? anc.legalEntity : "";
          const country = le.replace("drivalia_", "");
          return { ...anc, label: `${anc.label} (${country})` };
        }
        return anc;
      });
    }

    setAncillaryListFull(ancillaryListDisambiguated);
    setAncillaryList(ancillaryListDisambiguated);
  };

  //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
  const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
    (el: SelectElementModelBE) => ({
      code: el.name,
      label: t("translation:legalEntities.".concat(el.name))
    })
  );

  //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
  const optionsFiltered = legalEntityOptions
    .filter(function(el) {
      return el.code !== "all";
    });

  const everyLegalEntitiesWithoutAll = legalEntityOptions
    .map((el) => el.code as string)
    .filter((el) => el !== "all");

  //setto tutti i dealers options
  const dealerOption: SelectElementModelFE[] = user.dealers?.map(
    (el: SelectElementModelBE) => ({
      code: el.name,
      label: t("translation:dealers.".concat(el.name))
    })
  );

  const dealerFilteredOptions = dealerOption
    .map((el) => el.code as string)
    .filter((el) => el !== "all");

  const [testFilters, setTestFilters] = useState<FormSearchAncillaryExceptionModel>({
    commercialCategoryId: null,
    commercialSegmentId: null,
    name: "",
    productCode: "",
    ancillaryId: null,
    reservationFrom: null,
    reservationTo: null,
    pickupDateFrom: null,
    pickupDateTo: null,
    showOnlyActive: false,
    dealers: dealerOption,
    legalEntities: legalEntityOptions
  });

  // Use Effect
  useEffect(() => {
    handleGetStaticContent();
  }, []);

  // ITDRIV-20190 / LPP-23039: filtro client-side degli accessori al cambio di LE/Dealer.
  // La lista completa (ancillaryListFull) e' caricata una sola volta dal backend gia' filtrata
  // per il perimetro dell'utente; qui si restringe ulteriormente in base alla selezione dei filtri.
  useEffect(() => {
    if (!ancillaryListFull.length) return;

    const selectedLE: string[] = Array.isArray(testFilters.legalEntities)
      ? (testFilters.legalEntities as SelectElementModelFE[])
          .map((le) => le.code as string)
          .filter((code) => code !== "all")
      : [];
    const selectedDealers: string[] = Array.isArray(testFilters.dealers)
      ? (testFilters.dealers as SelectElementModelFE[])
          .map((d) => d.code as string)
          .filter((code) => code !== "all")
      : [];

    // Ancillary si popola COMBINANDO Legal Entity E Dealer.
    // Se una delle due condizioni non e' soddisfatta -> lista vuota + reset selezione.
    if (selectedLE.length === 0 || selectedDealers.length === 0) {
      setAncillaryList([]);
      setTestFilters((prev) => ({ ...prev, ancillaryId: null }));
      return;
    }

    const filtered = ancillaryListFull.filter((anc) => {
      const ancLE: string[] = Array.isArray(anc.legalEntity)
        ? (anc.legalEntity as string[])
        : anc.legalEntity
          ? [anc.legalEntity as string]
          : [];
      const ancDealer: string[] = Array.isArray(anc.dealer)
        ? (anc.dealer as string[])
        : anc.dealer
          ? [anc.dealer as string]
          : [];
      const leMatch = ancLE.length === 0 || ancLE.some((le) => selectedLE.includes(le));
      const dealerMatch = ancDealer.length === 0 || ancDealer.some((d) => selectedDealers.includes(d));
      return leMatch && dealerMatch;
    });

    setAncillaryList(filtered);
    // Se l'accessorio selezionato non e' piu' nel sottoinsieme filtrato, resettarlo
    if (testFilters.ancillaryId) {
      const currentCode = (testFilters.ancillaryId as SelectElementModelFE)?.code;
      if (currentCode && !filtered.some((a) => a.code === currentCode)) {
        setTestFilters((prev) => ({ ...prev, ancillaryId: null }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testFilters.legalEntities, testFilters.dealers, ancillaryListFull]);

  /* AG GRID - START */
  const { setServerSideDatasource, handleTriggerFilters, refresh } =
    useAgGridCustomHook<
      AncillaryExceptionEntityResponse,
      FormSearchAncillaryExceptionModel
    >({
      gridRef,
      api: getAncillaryExceptionsPaged,
      initialSortColId: "name",
      entityFilters: {
        commercialCategoryId: null,
        commercialSegmentId: null,
        name: "",
        productCode: "",
        ancillaryId: null,
        reservationFrom: null,
        reservationTo: null,
        pickupDateFrom: null,
        pickupDateTo: null,
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
      },
      customSort: "AncillaryExceptionEntityResponse"
    });

  // ITDRIV-20190 / LPP-23039: wrapper di onGridReady che imposta il filterModel con i filtri
  // correnti dell'utente PRIMA di settare il datasource. Cosi' la prima getRows parte gia' filtrata.
  const setServerSideDatasourceFiltersCustom = (params: any) => {
    const legalEntitiesArray = Array.isArray(testFilters.legalEntities)
      ? (testFilters.legalEntities as SelectElementModelFE[]).map((le: SelectElementModelFE) => le.code as string)
      : [];
    const dealersArray = Array.isArray(testFilters.dealers)
      ? (testFilters.dealers as SelectElementModelFE[]).map((d: SelectElementModelFE) => d.code as string)
      : [];

    const filters = {
      commercialCategoryId: (testFilters.commercialCategoryId as SelectElementModelFE)?.code?.toString() || "",
      commercialSegmentId: (testFilters.commercialSegmentId as SelectElementModelFE)?.code?.toString() || "",
      name: testFilters.name || "",
      reservationFrom: testFilters.reservationFrom ? dayjs(testFilters.reservationFrom).toISOString() : null,
      reservationTo: testFilters.reservationTo ? dayjs(testFilters.reservationTo).toISOString() : null,
      pickupDateFrom: testFilters.pickupDateFrom ? dayjs(testFilters.pickupDateFrom).toISOString() : null,
      pickupDateTo: testFilters.pickupDateTo ? dayjs(testFilters.pickupDateTo).toISOString() : null,
      productCode: testFilters.productCode || "",
      ancillaryId: (testFilters.ancillaryId as SelectElementModelFE)?.code as number,
      showOnlyActive: testFilters.showOnlyActive,
      legalEntities: legalEntitiesArray,
      dealers: dealersArray
    };

    const hardcodedFilter = Object.entries(filters).reduce(
      (acc: any, [key, value]) => ({
        ...acc,
        [key]: { type: "contains", filter: value }
      }),
      {}
    );

    params.api.setFilterModel(hardcodedFilter);
    setServerSideDatasource(params);
  };

  const handleTriggerFiltersInitial = () => {
    const legalEntitiesArray = Array.isArray(testFilters.legalEntities)
      ? (testFilters.legalEntities as SelectElementModelFE[]).map((le: SelectElementModelFE) => le.code as string)
      : [];
    
    const dealersArray = Array.isArray(testFilters.dealers)
      ? (testFilters.dealers as SelectElementModelFE[]).map((d: SelectElementModelFE) => d.code as string)
      : [];

    handleTriggerFilters({
          commercialCategoryId:
            (
              testFilters.commercialCategoryId as SelectElementModelFE
            )?.code?.toString() || "",
          commercialSegmentId:
            (
              testFilters.commercialSegmentId as SelectElementModelFE
            )?.code?.toString() || "",
          name: testFilters.name,
          reservationFrom: testFilters.reservationFrom
            ? dayjs(testFilters.reservationFrom).toISOString()
            : testFilters.reservationFrom,
          reservationTo: testFilters.reservationTo
            ? dayjs(testFilters.reservationTo).toISOString()
            : testFilters.reservationTo,
          pickupDateFrom: testFilters.pickupDateFrom
            ? dayjs(testFilters.pickupDateFrom).toISOString()
            : testFilters.pickupDateFrom,
          pickupDateTo: testFilters.pickupDateTo
            ? dayjs(testFilters.pickupDateTo).toISOString()
            : testFilters.pickupDateTo,
          productCode: testFilters.productCode,
          ancillaryId: (
            testFilters.ancillaryId as SelectElementModelFE
          )?.code as number,
          showOnlyActive: testFilters.showOnlyActive,
          legalEntities: legalEntitiesArray,
          dealers: dealersArray
        });
  };

  // useEffect(() => {
  //     setTimeout(() => {
  //         handleTriggerFiltersInitial();
  //     }, 500);
  // }, [i18n.resolvedLanguage]);

  const handleTriggerResetFilters = () => {
    setTestFilters({
      commercialCategoryId: null,
      commercialSegmentId: null,
      name: "",
      productCode: "",
      ancillaryId: null,
      reservationFrom: null,
      reservationTo: null,
      pickupDateFrom: null,
      pickupDateTo: null,
      showOnlyActive: false,
      dealers: dealerOption,
      legalEntities: legalEntityOptions
    });
    handleTriggerFilters({
      commercialCategoryId: null,
      commercialSegmentId: null,
      name: "",
      productCode: "",
      ancillaryId: null,
      reservationFrom: null,
      reservationTo: null,
      pickupDateFrom: null,
      pickupDateTo: null,
      dealers: dealerFilteredOptions,
      legalEntities: everyLegalEntitiesWithoutAll
    });
  };

  useEffect(() => {
    if (gridRef?.current) {
      const legalEntitiesArray = Array.isArray(testFilters.legalEntities)
        ? (testFilters.legalEntities as SelectElementModelFE[]).map((le: SelectElementModelFE) => le.code as string)
        : [];
      
      const dealersArray = Array.isArray(testFilters.dealers)
        ? (testFilters.dealers as SelectElementModelFE[]).map((d: SelectElementModelFE) => d.code as string)
        : [];
      handleTriggerFilters({
        commercialCategoryId:
          (
            testFilters.commercialCategoryId as SelectElementModelFE
          )?.code?.toString() || "",
        commercialSegmentId:
          (
            testFilters.commercialSegmentId as SelectElementModelFE
          )?.code?.toString() || "",
        name: testFilters.name,
        reservationFrom: testFilters.reservationFrom
          ? dayjs(testFilters.reservationFrom).toISOString()
          : testFilters.reservationFrom,
        reservationTo: testFilters.reservationTo
          ? dayjs(testFilters.reservationTo).toISOString()
          : testFilters.reservationTo,
        pickupDateFrom: testFilters.pickupDateFrom
          ? dayjs(testFilters.pickupDateFrom).toISOString()
          : testFilters.pickupDateFrom,
        pickupDateTo: testFilters.pickupDateTo
          ? dayjs(testFilters.pickupDateTo).toISOString()
          : testFilters.pickupDateTo,
        productCode: testFilters.productCode,
        ancillaryId: (
          testFilters.ancillaryId as SelectElementModelFE
        )?.code as number,
        showOnlyActive: testFilters.showOnlyActive,
        legalEntities: legalEntitiesArray,
        dealers: dealersArray
      });
    }
  }, [testFilters.showOnlyActive]);


  const { actionColDef } = useActionColDef();
  const columnDefs: ColDef[] = [
    {
      field: "legalEntities",
      headerName: t("pricing:ancillaryException.table.labels.legalEntities"),
      headerTooltip: t("pricing:ancillaryException.table.labels.legalEntities"),
      editable: false,
      flex: 1,
      filter: "text",
      cellRenderer: (params: ICellRendererParams) => t("translation:legalEntities.".concat(params.data.legalEntity)),
      hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
    },
    {
      field: "dealers",
      headerName: t("pricing:ancillaryException.table.labels.dealers"),
      headerTooltip: t("pricing:ancillaryException.table.labels.dealers"),
      editable: false,
      flex: 1,
      filter: "text",
      cellRenderer: (params: ICellRendererParams) => t("translation:dealers.".concat(params.data.dealer)),
      hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
    },
    {
      field: "name",
      headerName: `${t("pricing:ancillaryException.table.labels.name")}`,
      headerTooltip: `${t("pricing:ancillaryException.table.labels.name")}`,
      flex: 1,
      filter: "text"
    },
    {
      field: "commercialSegment.name",
      colId: "commercialSegmentId",
      valueFormatter: (params) => params.value,
      headerName: t("pricing:ancillaryException.form.labels.commercialSegment"),
      headerTooltip: t("pricing:ancillaryException.form.labels.commercialSegment"),
      flex: 1,
      filter: "text"
    },
    {
      field: "commercialCategory",
      colId: "commercialCategoryId",
      valueFormatter: (params) => {
                const category = params.value;
                const legalEntity = params.data?.legalEntity;
                
                if (!category) {
                    return '';
                }
                
                if (!category.labels || category.labels.length === 0) {
                    return category.name || '';
                }
                
                const labelObj = category.labels.find((l: any) => l.country === legalEntity);
                return labelObj?.label || category.name || '';
            },
      headerName: t("pricing:ancillaryException.table.labels.commercialCategory"),
      headerTooltip: t("pricing:ancillaryException.table.labels.commercialCategory"),
      flex: 1,
      filter: "text"
    },
    {
      field: "prcAncillary.nameAncillary",
      colId: "ancillaryId",
      headerName: `${t("pricing:ancillaryException.table.labels.ancillary")}`,
      headerTooltip: `${t("pricing:ancillaryException.table.labels.ancillary")}`,
      flex: 1,
      filter: "text",
      cellRenderer: (params: ICellRendererParams) => {
        // LPP-21049: Skip defaultLabel fallback to avoid showing description instead of name
        const multilanguage = params.data?.prcAncillary?.prcMultilanguage;
        if (multilanguage && multilanguage[i18n.language]) {
          return multilanguage[i18n.language];
        }
        return params.data?.prcAncillary?.nameAncillary;
      }
    },
    {
      field: "relatedDiscountIncreasePercentage",
      headerName: t("pricing:rate.form.labels.referenceTariffLabels.discountIncrease"),
      headerTooltip: t("pricing:rate.form.labels.referenceTariffLabels.discountIncrease"),
      flex: 1,
      filter: "text",
      cellRenderer: (params: any) => {
        return params.data.relatedDiscountIncreasePercentage ?
          i18nConvertNumberWithDecimals(params.data.relatedDiscountIncreasePercentage) + "%"
          :
          params.data.relatedDiscountIncreaseFixed
            ?
            i18nConvertNumberWithDecimals(Number(params.data.relatedDiscountIncreaseFixed) / 100)
            :
            null;
      }
    },
    {
      field: "reservationFrom",
      headerName: t("pricing:ancillaryException.table.labels.reservationFrom"),
      headerTooltip: t("pricing:ancillaryException.table.labels.reservationFrom"),
      flex: 1,
      valueFormatter: (params: any) => params.data.reservationFrom ? dateFormatter(params.data.reservationFrom) : "",
      filter: "text"
    },
    {
      field: "reservationTo",
      headerName: t("pricing:ancillaryException.table.labels.reservationTo"),
      headerTooltip: t("pricing:ancillaryException.table.labels.reservationTo"),
      flex: 1,
      valueFormatter: (params: any) => params.data.reservationTo ? dateFormatter(params.data.reservationTo) : "",
      filter: "text"
    },
    {
      field: "pickupDateFrom",
      headerName: t("pricing:ancillaryException.table.labels.pickupFrom"),
      headerTooltip: t("pricing:ancillaryException.table.labels.pickupFrom"),
      flex: 1,
      valueFormatter: (params: any) => params.data.pickupFrom ? dateFormatter(params.data.pickupFrom) : "",
      filter: "text"
    },
    {
      field: "pickupDateTo",
      headerName: t("pricing:ancillaryException.table.labels.pickupTo"),
      headerTooltip: t("pricing:ancillaryException.table.labels.pickupTo"),
      flex: 1,
      valueFormatter: (params: any) => params.data.pickupTo ? dateFormatter(params.data.pickupTo) : "",
      filter: "text"
    },
    {
      field: "isOneWay",
      headerName: "One Way",
      headerTooltip: "One Way",
      flex: 1,
      cellRenderer: (params: any) => {
        return params.data.prcAncillary.isOneWay ?
          <Chip label={t("pricing:labels.yes")} sx={{ bgcolor: "#BEE5CA" }} variant={"filled"} />
          :
          null;
        // <Chip label={ t("pricing:labels.no") } sx={ { bgcolor: "#F39D9D" } } variant={ "filled" } />;
      },
      sortable: false
    },
    {
      field: "showOnlyActive",
      filter: "text",
      hide: true
    },
    {
      ...actionColDef,
      cellRenderer: (params: ICellRendererParams) => (
        <ActionsPopover
          url={`../ancillary/ancillary-exception/create/${params.data.id}`}
          params={params}
          view={{
            action: viewButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
              return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_VIEW_DETAIL_ANCILLARY_EXCEPTION";
              });
            })
          }}
          update={!params.data.prcAncillary.isOneWay ? {
            action: updateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
              return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_UPDATE_ANCILLARY_EXCEPTION";
              });
            })
          } : undefined}
          terminate={{
            action: terminateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
              return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_TERMINATE_ANCILLARY_EXCEPTION";
              });
            })
          }}
          del={!params.data.used ? {
            action: deleteButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
              return profile.auths.find((auth: any) => {
                return auth.name === "SCOPE_PRC_DELETE_ANCILLARY_EXCEPTION";
              });
            })
          } : undefined}
        />
      )
    }
  ];

  const defaultColDef = DEFAULT_COL_DEF;
  /* AG GRID - END */

  /* TABLE ACTIONS - START */
  const viewButtonClicked = (data: AncillaryExceptionEntityResponse) => {
    setViewAncillaryException(true);
    setRowIndex(data.id);
  };

  const updateButtonClicked = (data: AncillaryExceptionEntityResponse) => {
    // navigate("../ancillary/ancillary-exception/create", {
    //     replace: true,
    //     state: {ancillaryExceptionId: data.id}
    // });
  };

  const terminate = async (id: number) => {
    await terminateAncillaryException(id);

    setDialog(null);
    dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    refresh();
  };

  const terminateButtonClicked = async (data: AncillaryEntityResponse) => {
    setDialog({
      open: true,
      onClickConfirm: () => terminate(data.id),
      message: t(
        "pricing:ancillaryExceptions.dialogContents.terminateAncillaryExceptionInfo"
      )
    });
  };

  const deleteButtonClicked = (data: AncillaryEntityResponse) => {
    setDialog({
      open: true,
      onClickConfirm: () => handleConfirmDelete(data.id),
      message: t(
        "pricing:ancillaryExceptions.dialogContents.removeAncillaryExceptionInfo"
      )
    });
  };

  const handleConfirmDelete = async (id: number) => {
    try {
      if (id) {
        await deleteAncillaryException(id);
        setDialog(null);
        await refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
      }
    } catch (e) {
    }
  };
  /* TABLE ACTIONS - END */

  const toolbar = {
    renderActions: () => (
      <BasicToolbar
        addIcon={
          !!user.profiles.find((profile: any) => {
            return profile.auths.find((auth: any) => {
              return auth.name === "SCOPE_PRC_CREATE_ANCILLARY_EXCEPTION";
            });
          }) ?
            {
              onClick: () => {
              }
            } : undefined}
        activeSwitch={{
          label: t("pricing:rentalPeriods.form.labels.showActive"),
          handleChange: (checked: boolean) =>
            setTestFilters({
              ...testFilters,
              showOnlyActive: checked
            }),
          value: testFilters.showOnlyActive as boolean
        }}
        url={"../ancillary/ancillary-exception/create/new"}
      />
    )
  };

  return (
    <Box>
      <SectionHeader
        title={t("translation:auths.SCOPE_PRC_VIEW_ANCILLARY_EXCEPTION.name")}
      />
      <AncillaryExceptionFilters
        handleClickSearch={handleTriggerFiltersInitial}
        handleClickResetSearch={handleTriggerResetFilters}
        commercialCategoryListOriginal={commercialCategoryListOriginal}
        testFilters={testFilters}
        setTestFilters={setTestFilters}
      />

      <Grid container spacing={2} p={2}>
        {viewAncillaryException && rowIndex ? (
            <AncillaryExceptionContainerView
              ancillaryExceptionId={rowIndex}
              open={viewAncillaryException}
              setOpen={setViewAncillaryException}
            />
          ) :
          <Grid item xs={12} sm={12}>
            <AgGridCustomTable
              gridRef={gridRef}
              toolbar={toolbar}
              defaultColDef={defaultColDef}
              columnDefs={columnDefs}
              pagination
              localeText={agGridLocale}
              tooltipShowDelay={0}
              domLayout="autoHeight"
              rowModelType="serverSide"
              paginationPageSize={10}
              cacheBlockSize={10}
              serverSideInfiniteScroll
              onGridReady={setServerSideDatasourceFiltersCustom}
            />
          </Grid>
        }
        {dialog?.open && (
          <DialogComponent
            type={"info"}
            onClickConfirm={dialog.onClickConfirm}
            setOpen={() => setDialog(null)}
            open={dialog.open}
            message={dialog.message}
          />
        )}
      </Grid>

      <Loading />
    </Box>
  );
};

export default AncillaryExceptions;
